import Box from "@mui/material/Box"
import Drawer from "@mui/material/Drawer"
import {
  FocusContext,
  setFocus,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, { useEffect, useMemo } from "react"
import { playerStore } from "src/store/PlayerStore"

import { useModalDismiss } from "../../../../hooks/use-modal-dismiss"
import useTranslate, { Namespaces } from "../../../../hooks/use-translate"
import { TranslationKey } from "../../../../types"
import { s } from "../../../../utilities/common"
import { canPlayFlacAudio } from "../../../../utilities/constants"
import { AudioStreamQualities } from "../../../../utilities/streaming-helpers"
import { theme, TI } from "../../../../utilities/theme"
import { DGText } from "../../../common/DGText"
import { PlayerUIFocusKeys } from "../../player-ui-focus-keys"
import { usePlayerTracking } from "../../use-player-tracking"

import { CheckItem } from "./SubSelect"

const mapTrackName = (name: keyof typeof AudioStreamQualities) => {
  return {
    [AudioStreamQualities.atmos]: "mediaQuality__title_audio_atmos",
    [AudioStreamQualities.lossless]: "mediaQuality__title_audio_lossless",
    [AudioStreamQualities.stereoGood]: "mediaQuality__title_audio_normal",
    [AudioStreamQualities.stereoHigh]: "mediaQuality__title_audio_high",
    [AudioStreamQualities.sd]: "mediaQuality__title_audio_normal",
    [AudioStreamQualities.hd]: "mediaQuality__title_audio_high",
  }[name] as TranslationKey<Namespaces.Default> | undefined
}

export const StreamingQualitySelect = () => {
  const hls = playerStore((state) => state.hls)
  const setStreamingQualityOpen = playerStore(
    (state) => state.setStreamingQualityOpen,
  )
  const streamingQualityOpen = playerStore(
    (state) => state.streamingQualityOpen,
  )
  const audioTrack = playerStore((state) => state.audioTrack)
  const navigationProps = playerStore((state) => state.navigationProps)
  const setNavigationProps = playerStore((state) => state.setNavigationProps)
  const resetPlayback = playerStore((state) => state.resetPlayback)
  const track = usePlayerTracking("audio_quality_available")
  const trackSelect = usePlayerTracking("audio_quality_selected")

  const { focusKey, ref } = useFocusable({
    isFocusBoundary: true,
  })

  const t = useTranslate()

  useModalDismiss(() => {
    setFocus(PlayerUIFocusKeys.StreamQuality)
    setStreamingQualityOpen(false)
  })

  useEffect(() => {
    if (streamingQualityOpen) {
      setFocus(PlayerUIFocusKeys.TrackPrefix + audioTrack)
      track({
        audio_quality_available: hls?.allAudioTracks
          .map((e) => e.name)
          .join("/"),
      })
    }
  }, [streamingQualityOpen, audioTrack, hls])
  const canPlayDolbyAtmosAudio = useMemo(
    () => window.capabilities.dolbyAtmos,
    [],
  )

  if (!audioTrack || !hls) return null
  return (
    <div>
      <>
        <Drawer
          anchor={"right"}
          open={streamingQualityOpen}
          onClose={() => setStreamingQualityOpen(false)}
        >
          <Box
            sx={{
              width: s("794px"),
              backgroundColor: theme.palette.primary.main,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              pt: s("120px"),
            }}
            role="presentation"
            onClick={() => {}}
          >
            <DGText
              tx={TI.H05tv}
              sx={{
                color: theme.palette.white.main,
                pb: s("66px"),
                pl: s("56px"),
              }}
            >
              {t("player__settings_audio")}
            </DGText>
            <FocusContext.Provider value={focusKey}>
              <Box ref={ref}>
                {hls.allAudioTracks
                  .filter(
                    (track) =>
                      track.name !== AudioStreamQualities.lossless ||
                      canPlayFlacAudio,
                  )
                  .filter(
                    (track) =>
                      track.name !== AudioStreamQualities.atmos ||
                      canPlayDolbyAtmosAudio,
                  )
                  .map((track) => {
                    const trackName = mapTrackName(
                      track.name as keyof typeof AudioStreamQualities,
                    )
                    const title = trackName ? t(trackName) : track.name
                    return (
                      <CheckItem
                        key={"mediaQuality_" + track.name}
                        ariaLabel={title}
                        onPress={() => {
                          setStreamingQualityOpen(false)
                          setFocus(PlayerUIFocusKeys.PlayButton)
                          if (track.name === audioTrack) return
                          trackSelect({
                            audio_quality_available: hls?.allAudioTracks
                              .map((e) => e.name)
                              .join("/"),
                            audio_quality_selected: track.name,
                          })
                          // restart playback because SmartTV can't handle track switches during playback
                          setNavigationProps({
                            ...navigationProps,
                            startAudioTrack: track.name,
                          })
                          resetPlayback()
                          hls?.setAudioOption(track)
                        }}
                        selected={audioTrack === track.name}
                        value={track.name}
                        focusPrefix={PlayerUIFocusKeys.TrackPrefix}
                      >
                        <DGText
                          tx={TI.Subheadline05tv}
                          sx={{
                            color: theme.palette.white.main,
                          }}
                        >
                          {title}
                        </DGText>
                      </CheckItem>
                    )
                  })}
              </Box>
            </FocusContext.Provider>
          </Box>
        </Drawer>
      </>
    </div>
  )
}
